import { PlusOutlined } from '@ant-design/icons';
import { Form, FormInstance } from 'antd';
import {
  Checkbox,
  DatePicker,
  Input,
  Select,
  Table,
} from 'UI';
import CultureSelectSearch from 'components/CultureSelectSearch';
import {
  Culture,
  Order,
  Product,
  Tray,
  Variety,
} from 'constants/interface';
import { InventoryTypeDisplayMapping, InventoryTypeEnum } from 'constants/inventory';
import { ProductTypeEnum } from 'constants/product';
import { useDispatch } from 'hooks/store';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  cleanCulturesAction,
  getCultureProductsAction,
  setCulturesAction,
  setProductsAction,
} from 'state/slices/products';
import {
  getTrayListAction,
  isLoadingTraysSelector,
  traysEntitiesSelector,
  traysIdsSelector,
} from 'state/slices/tray';
import {
  OrderOtherStatusEnum,
  OrderTypeEnum,
  OrderUnitDisplayMapping,
  OrderUnitEnum,
} from 'constants/order';
import dayjs, { Dayjs } from 'dayjs';
import CultureProductSelect from 'components/CultureProductSelect';
import { useProducts } from 'hooks/products';
import { calculateSeedQuantity } from 'helpers/order';

import {
  FormContainer,
  RowFormItem,
  AddRowButton,
  NotesInput,
  RowInfoContainer,
  RemoveRowButton,
  RemoveRowIcon,
  RowIndexLabel,
  RowSmallFormItem,
  RowContentFitFormItem,
  TableFooterContainer,
  OrderPriceContainer,
  RegularOrderTable,
} from './style';

interface RegularOrderItemFormData {
  culture: string
  // variety: string
  product: string
  tray: string
  seedSource: InventoryTypeEnum
  pruning: boolean
  unit: OrderUnitEnum
  orderQuantity: string
  plannedSeedDate: Dayjs
  plannedDeliveryDate: Dayjs
  calculatedSeedQuantity: string
  unitPrice: string
  totalPrice: string
  notes?: string
}

export interface RegularOrderItemsFormData {
  items: RegularOrderItemFormData[]
}

interface RegularTableProps {
  formHandler: FormInstance<RegularOrderItemsFormData>
  isLoading: boolean
  initialOrder?: Order
}

const createDefaultRow = () => ({
  seedSource: InventoryTypeEnum.CLIENT,
  unit: OrderUnitEnum.THOUSAND,
});

const RegularTable = ({
  formHandler,
  isLoading: externalLoading,
  initialOrder,
}: RegularTableProps): JSX.Element => {
  const dispatch = useDispatch();

  const [selectedCultures, setSelectedCultures] = useState<(Culture | undefined)[]>([]);
  const [selectedVarieties, setSelectedVarieties] = useState<(Variety | undefined)[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<(Product | undefined)[]>([]);
  const [selectedTrays, setSelectedTrays] = useState<(Tray | undefined)[]>([]);
  const [rowTotalPrices, setRowTotalPrices] = useState<(number | undefined)[]>([]);

  const [orderTotalPrice, setOrderTotalPrice] = useState(0);

  const products = useProducts({
    types: [ProductTypeEnum.STANDARD],
  });

  const memoizedInitialOrder = useMemo(() => {
    if (initialOrder) {
      return { ...initialOrder };
    }

    return undefined;
  }, [initialOrder?.id]);

  // Initial Order
  useEffect(() => {
    if (
      memoizedInitialOrder
      && memoizedInitialOrder.type === OrderTypeEnum.REGULAR
    ) {
      const updatedSelectedCultures: (Culture | undefined)[] = [];
      const updatedSelectedVarieties: (Variety | undefined)[] = [];
      const updatedSelectedProducts: (Product | undefined)[] = [];
      const updatedSelectedTrays: (Tray | undefined)[] = [];
      const updatedRowTotalPrices: (number | undefined)[] = [];
      const updatedFormItems = memoizedInitialOrder
        .items
        .map((initialOrderItem) => {
          const prodItem = initialOrderItem
            .productionItems
            .find((curProdItem) => curProdItem.type === ProductTypeEnum.STANDARD);

          updatedSelectedCultures.push(prodItem?.product?.variety?.culture);
          updatedSelectedVarieties.push(prodItem?.product?.variety);
          updatedSelectedProducts.push(prodItem?.product);
          updatedSelectedTrays.push(prodItem?.tray);

          const updatedTotalPrice = (
            (initialOrderItem.orderQuantity * initialOrderItem.unitPrice) / 100
          );
          updatedRowTotalPrices.push(updatedTotalPrice);

          const calculatedSeedQuantity = prodItem
            ? `${calculateSeedQuantity(
              initialOrderItem.orderQuantity,
              initialOrderItem.unit,
              prodItem?.product,
              prodItem?.tray,
            )}`
            : '0';

          const itemFormData = {
            culture: prodItem?.product.variety.culture.id,
            variety: prodItem?.product.variety.id,
            product: prodItem?.product.id,
            tray: prodItem?.tray.id,
            seedSource: prodItem?.seedSource,
            pruning: initialOrderItem.pruning,
            unit: initialOrderItem.unit,
            orderQuantity: `${initialOrderItem.orderQuantity}`,
            plannedSeedDate: dayjs(initialOrderItem.plannedSeedDate),
            plannedDeliveryDate: dayjs(initialOrderItem.plannedDeliveryDate),
            calculatedSeedQuantity,
            unitPrice: `${initialOrderItem.unitPrice / 100}`,
            notes: initialOrderItem.notes,
          };

          return itemFormData;
        });

      const updatedOrderTotalPrice = updatedRowTotalPrices
        .reduce((acc, curRowPrice) => {
          const newValue = (acc || 0) + (curRowPrice || 0);
          return newValue;
        }, 0);

      setOrderTotalPrice(updatedOrderTotalPrice || 0);

      setSelectedCultures(updatedSelectedCultures);
      setSelectedVarieties(updatedSelectedVarieties);
      setSelectedProducts(updatedSelectedProducts);
      setSelectedTrays(updatedSelectedTrays);
      setRowTotalPrices(updatedRowTotalPrices);

      dispatch(setCulturesAction(
        updatedSelectedCultures.filter((culture) => !!culture) as Culture[],
      ));
      dispatch(setProductsAction(
        updatedSelectedProducts.filter((product) => !!product) as Product[],
      ));

      formHandler.setFieldsValue({
        items: updatedFormItems,
      });
    } else {
      formHandler.setFieldsValue({
        items: [createDefaultRow()],
      });
    }
  }, [memoizedInitialOrder]);

  // Rows management

  const onRowAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    addedRowIndex: number,
  ) => {
    const updatedSelectedCultures = [
      ...selectedCultures.slice(0, addedRowIndex),
      undefined,
    ];
    setSelectedCultures(updatedSelectedCultures);

    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, addedRowIndex),
      undefined,
    ];
    setSelectedVarieties(updatedSelectedVarieties);

    const updatedSelectedProducts = [
      ...selectedProducts.slice(0, addedRowIndex),
      undefined,
    ];
    setSelectedProducts(updatedSelectedProducts);

    const updatedSelectedTrays = [
      ...selectedTrays.slice(0, addedRowIndex),
      undefined,
    ];
    setSelectedTrays(updatedSelectedTrays);

    const updatedRowTotalPrices = [
      ...rowTotalPrices.slice(0, addedRowIndex),
      undefined,
    ];
    setRowTotalPrices(updatedRowTotalPrices);

    formAdd(createDefaultRow(), addedRowIndex);
  };

  const onRowRemoved = (
    formRemove: (index: number) => void,
    removedRowIndex: number,
  ) => {
    const { items: formItems } = formHandler.getFieldsValue();
    if (formItems.length < 2) {
      return;
    }

    const updatedSelectedCultures = [
      ...selectedCultures.slice(0, removedRowIndex),
      ...selectedCultures.slice(removedRowIndex + 1),
    ];
    setSelectedCultures(updatedSelectedCultures);

    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, removedRowIndex),
      ...selectedVarieties.slice(removedRowIndex + 1),
    ];
    setSelectedVarieties(updatedSelectedVarieties);

    const updatedSelectedProducts = [
      ...selectedProducts.slice(0, removedRowIndex),
      ...selectedProducts.slice(removedRowIndex + 1),
    ];
    setSelectedProducts(updatedSelectedProducts);

    const updatedSelectedTrays = [
      ...selectedTrays.slice(0, removedRowIndex),
      ...selectedTrays.slice(removedRowIndex + 1),
    ];
    setSelectedTrays(updatedSelectedTrays);

    const updatedRowTotalPrices = [
      ...rowTotalPrices.slice(0, removedRowIndex),
      ...rowTotalPrices.slice(removedRowIndex + 1),
    ];
    setRowTotalPrices(updatedRowTotalPrices);

    const updatedOrderTotalPrice = updatedRowTotalPrices
      .reduce((acc, curRowPrice) => {
        const newValue = (acc || 0) + (curRowPrice || 0);
        return newValue;
      }, 0);

    setOrderTotalPrice(updatedOrderTotalPrice || 0);

    formRemove(removedRowIndex);
  };

  // Culture handling

  const onCultureSelect = (culture: Culture, selectedCultureIndex: number) => {
    dispatch(cleanCulturesAction());

    const updatedSelectedCultures = [
      ...selectedCultures.slice(0, selectedCultureIndex),
      culture,
      ...selectedCultures.slice(selectedCultureIndex + 1),
    ];

    setSelectedCultures(updatedSelectedCultures);

    // Reset varieties

    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, selectedCultureIndex),
      undefined,
      ...selectedVarieties.slice(selectedCultureIndex + 1),
    ];

    setSelectedVarieties(updatedSelectedVarieties);

    const updatedRowTotalPrices = [
      ...rowTotalPrices.slice(0, selectedCultureIndex),
      undefined,
      ...rowTotalPrices.slice(selectedCultureIndex + 1),
    ];
    setRowTotalPrices(updatedRowTotalPrices);

    const updatedOrderTotalPrice = updatedRowTotalPrices
      .reduce((acc, curRowPrice) => {
        const newValue = (acc || 0) + (curRowPrice || 0);
        return newValue;
      }, 0);

    setOrderTotalPrice(updatedOrderTotalPrice || 0);

    const { items: formItems } = formHandler.getFieldsValue();
    const updatedItems = [
      ...formItems.slice(0, selectedCultureIndex),
      {
        ...formItems[selectedCultureIndex],
        product: undefined,
        orderQuantity: undefined,
        plannedSeedDate: undefined,
        plannedDeliveryDate: undefined,
        calculatedSeedQuantity: undefined,
        unitPrice: undefined,
      },
      ...formItems.slice(selectedCultureIndex + 1),
    ];
    formHandler.setFieldsValue({
      items: updatedItems,
    });

    dispatch(getCultureProductsAction({
      cultureId: culture.id,
      filterData: {
        isPaginated: false,
        isActive: true,
      },
    }));
  };

  // Varieties handling
  const onProductSelect = (selectedProductIndex: number, product?: Product) => {
    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, selectedProductIndex),
      product?.variety,
      ...selectedVarieties.slice(selectedProductIndex + 1),
    ];

    setSelectedVarieties(updatedSelectedVarieties);

    const updatedSelectedProducts = [
      ...selectedProducts.slice(0, selectedProductIndex),
      product,
      ...selectedProducts.slice(selectedProductIndex + 1),
    ];

    setSelectedProducts(updatedSelectedProducts);
  };

  // Tray options

  const trayEntities = useSelector(traysEntitiesSelector);
  const trayIds = useSelector(traysIdsSelector);
  const isLoadingTrays = useSelector(isLoadingTraysSelector);
  const trayList = trayIds
    .map((trayId) => trayEntities[trayId])
    .filter((tray) => !!tray) as Tray[];

  useEffect(() => {
    dispatch(getTrayListAction({
      isPaginated: false,
    }));
  }, []);

  const onTraySelect = (selectedTrayIndex: number, tray?: Tray) => {
    const updatedSelectedTrays = [
      ...selectedTrays.slice(0, selectedTrayIndex),
      tray,
      ...selectedTrays.slice(selectedTrayIndex + 1),
    ];

    setSelectedTrays(updatedSelectedTrays);
  };

  // Date range calculation

  const onDateChange = (index: number, field?: 'seed' | 'delivery') => {
    const currentFormItems: RegularOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];
    if (!row) {
      return;
    }
    const rowProduct = row.product;

    const curProduct = products
      .find((product) => product.id === rowProduct);

    if (!curProduct || !curProduct.periodFormation) {
      return;
    }

    const updatedDates = field === 'delivery'
      ? {
        plannedSeedDate: row
          .plannedDeliveryDate
          ?.subtract(curProduct.periodFormation, 'day'),
        plannedDeliveryDate: row
          .plannedDeliveryDate,
      } : {
        plannedSeedDate: row
          .plannedSeedDate,
        plannedDeliveryDate: row
          .plannedSeedDate
          ?.add(curProduct.periodFormation, 'day'),
      };

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...row,
        ...updatedDates,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  const updateSeedQuantity = (index: number) => {
    const currentFormItems: RegularOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];
    if (!row) {
      return;
    }

    const rowProduct = row.product;
    const curTray = trayEntities[row?.tray || ''];
    const curUnit = row.unit;
    const curQuantity = row.orderQuantity;

    if (!rowProduct || !curTray || !curUnit || !curQuantity) {
      return;
    }

    const curProduct = products
      .find((product) => product.id === rowProduct);

    if (!curProduct) {
      return;
    }

    const calculatedTotal = calculateSeedQuantity(
      parseInt(curQuantity, 10),
      curUnit,
      curProduct,
      curTray,
    );

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...row,
        calculatedSeedQuantity: `${
          Intl
            .NumberFormat('pt-BR')
            .format(calculatedTotal)}`,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  // Price handling
  const updateTotalPrice = (index: number) => {
    const currentFormItems: RegularOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];
    if (!row) {
      return;
    }

    const curQuantity = parseInt(row?.orderQuantity || '0', 10);
    const curUnitPrice = parseFloat(row.unitPrice || '0');

    if (!curQuantity || !curUnitPrice) {
      return;
    }

    const updatedTotalPrice = curQuantity * curUnitPrice;

    const updatedTotalPrices = [
      ...rowTotalPrices.slice(0, index),
      updatedTotalPrice,
      ...rowTotalPrices.slice(index + 1),
    ];
    setRowTotalPrices(updatedTotalPrices);

    const updatedOrderTotalPrice = updatedTotalPrices
      .reduce((acc, curRowPrice) => {
        const newValue = (acc || 0) + (curRowPrice || 0);
        return newValue;
      }, 0);

    setOrderTotalPrice(updatedOrderTotalPrice || 0);
  };

  const isActiveOrderCanceled = initialOrder?.status === OrderOtherStatusEnum.CANCELED;

  return (
    <FormContainer
      layout="vertical"
      form={formHandler}
      validateTrigger="onSubmit"
    >
      <Form.List
        name="items"
        rules={[{
          message: 'Insira pelo menos um item',
          validator: () => {
            // TODO: check this logic
            const formItems = [1];
            if (formItems.length > 0) {
              return Promise.resolve();
            }

            return Promise.reject();
          },
        }]}
      >
        {(formListItems, { add, remove }) => (
          <RegularOrderTable
            dataSource={formListItems}
            tableLayout="fixed"
            scroll={{ // TODO: adjust vertical scrolling
              x: true,
              y: 390,
            }}
            pagination={false}
            bordered
            footer={() => (
              <TableFooterContainer>
                <AddRowButton
                  icon={<PlusOutlined />}
                  onClick={() => onRowAdded(add, formListItems.length)}
                  disabled={externalLoading || isActiveOrderCanceled}
                >
                  Adicionar item
                </AddRowButton>
                <OrderPriceContainer>
                  <span>
                    {`Total orçamento: ${Intl
                      .NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                      .format(orderTotalPrice || 0)
                    }`}
                  </span>
                  <span>
                    {`Adiantamento: ${Intl
                      .NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                      .format((orderTotalPrice || 0) / 2)
                    }`}
                  </span>
                </OrderPriceContainer>
              </TableFooterContainer>
            )}
          >
            <Table.Column
              title=""
              // shouldCellUpdate={() => false}
              render={(cellValue, rowRecord, rowIndex) => (
                <RowInfoContainer>
                  <RemoveRowButton
                    disabled={externalLoading || isActiveOrderCanceled}
                    onClick={() => onRowRemoved(remove, rowIndex)}
                  >
                    <RemoveRowIcon />
                  </RemoveRowButton>
                  <RowIndexLabel>
                    {`#${rowIndex + 1}`}
                  </RowIndexLabel>
                </RowInfoContainer>
              )}
            />
            <Table.ColumnGroup
              title="Muda Pé Franco"
            >

              <Table.Column
                title="Cultura"
                // shouldCellUpdate={(
                //   record: RegularOrderItemFormData,
                //   prevRecord: RegularOrderItemFormData,
                // ) => record.culture !== prevRecord.culture}
                render={(cellValue, rowRecord, rowIndex) => (
                  <CultureSelectSearch
                    form={{
                      FormItem: RowFormItem,
                      formItemProps: {
                        name: [rowIndex, 'culture'],
                        rules: [{
                          required: true,
                          message: 'Selecione uma cultura',
                        }],
                      },
                    }}
                    onSelect={(culture) => onCultureSelect(culture, rowIndex)}
                    disabled={externalLoading || isActiveOrderCanceled}
                    initialSelected={selectedCultures[rowIndex]}
                  />
                )}
              />
              <Table.Column
                title="Variedade"
                render={(cellValue, rowRecord, rowIndex) => (
                  <>
                    <CultureProductSelect
                      cultureId={selectedCultures[rowIndex]?.id}
                      autoFetch={!initialOrder}
                      form={{
                        FormItem: RowFormItem,
                        formItemProps: {
                          name: [rowIndex, 'product'],
                          rules: [{
                            required: true,
                            message: 'Selecione uma variedade',
                          }],
                        },
                      }}
                      onSelect={(product) => onProductSelect(rowIndex, product)}
                      filter={{
                        productType: ProductTypeEnum.STANDARD,
                      }}
                      disabled={externalLoading || isActiveOrderCanceled}
                      initialSelected={selectedProducts[rowIndex]}
                    />
                  </>
                )}
              />
              <Table.Column
                title="Bdj"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'tray']}
                    rules={[
                      {
                        required: true,
                        message: 'Selecione uma bandeja',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione uma bandeja"
                      onChange={() => updateSeedQuantity(rowIndex)}
                      onSelect={(trayId) => onTraySelect(rowIndex, trayEntities[trayId])}
                      disabled={isLoadingTrays || externalLoading || isActiveOrderCanceled}
                      notFoundContent={<div>Nenhuma bandeja disponível</div>}
                    >
                      {trayList.map((trayOption) => (
                        <Select.Option
                          value={trayOption.id}
                          key={trayOption.id}
                        >
                          {`${trayOption.capacity}${trayOption.isHydroponic ? ' Hidro' : ''}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Semente"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'seedSource']}
                    required
                  >
                    <Select
                      placeholder="Selecione um estoque de semente"
                      notFoundContent={null}
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    >
                      {Object.values(InventoryTypeEnum).map((inventoryType) => (
                        <Select.Option
                          value={inventoryType}
                          key={inventoryType}
                        >
                          {InventoryTypeDisplayMapping[inventoryType].name}
                        </Select.Option>
                      ))}
                    </Select>
                  </RowFormItem>
                )}
              />
              <Table.Column
                title={<div>Despontar</div>}
                ellipsis
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowContentFitFormItem
                    name={[rowIndex, 'pruning']}
                    valuePropName="checked"
                  >
                    <Checkbox
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    />
                  </RowContentFitFormItem>
                )}
              />
              <Table.Column
                title="Unidade"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'unit']}
                    required
                  >
                    <Select
                      placeholder="Selecione uma unidade"
                      onChange={() => updateSeedQuantity(rowIndex)}
                      notFoundContent={null}
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    >
                      {Object
                        .values(OrderUnitEnum)
                        .filter((curUnit) => curUnit !== OrderUnitEnum.UNIT)
                        .map((curUnit) => (
                          <Select.Option
                            value={curUnit}
                            key={curUnit}
                          >
                            {OrderUnitDisplayMapping[curUnit].smallName}
                          </Select.Option>
                        ))}
                    </Select>
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Qtd"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'orderQuantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={1}
                      onChange={() => {
                        updateSeedQuantity(rowIndex);
                        updateTotalPrice(rowIndex);
                      }}
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Data semeio prev"
                width={180}
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'plannedSeedDate']}
                    // initialValue={dayjs()}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma data',
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                      onChange={() => onDateChange(rowIndex, 'seed')}
                    />
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Data entrega prev"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'plannedDeliveryDate']}
                    // initialValue={dayjs()}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma data',
                      },
                    ]}
                  >
                    <DatePicker
                      onChange={() => onDateChange(rowIndex, 'delivery')}
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    />
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Qtd semente prev"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'calculatedSeedQuantity']}
                  >
                    <Input type="text" disabled />
                  </RowSmallFormItem>
                )}
              />
            </Table.ColumnGroup>
            <Table.ColumnGroup
              title="Orçamento"
            >
              <Table.Column
                title="$Unit"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'unitPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um valor unitário',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      prefix="R$"
                      onChange={() => updateTotalPrice(rowIndex)}
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="$Total"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem>
                    <Input
                      type="text"
                      prefix="R$"
                      disabled
                      value={
                        Intl
                          .NumberFormat('pt-BR')
                          .format(rowTotalPrices[rowIndex] || 0)
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Obs"
                shouldCellUpdate={(
                  record: RegularOrderItemFormData,
                  prevRecord: RegularOrderItemFormData,
                ) => record.notes !== prevRecord.notes}
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'notes']}
                  >
                    <NotesInput
                      disabled={
                        externalLoading
                        || !selectedCultures[rowIndex]
                        || !selectedVarieties[rowIndex]
                        || !selectedTrays[rowIndex]
                        || isActiveOrderCanceled
                      }
                    />
                  </RowFormItem>
                )}
              />
            </Table.ColumnGroup>
          </RegularOrderTable>
        )}
      </Form.List>
    </FormContainer>
  );
};

export default RegularTable;
